import React, { useEffect, useState } from "react";
import {
  Drawer,
  Spin,
  Empty,
  Card,
  Table,
  Statistic,
  Row,
  Col,
  Tag,
  Divider,
  Button,
  Tooltip,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Alert,
} from "antd";
import {
  CheckCircleOutlined,
  WarningOutlined,
  PercentageOutlined,
  EditOutlined,
  DeleteOutlined,
  CreditCardOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import useStudentFees from "../../hooks/finance/useStudentFees";
import { formatCurrencyWithDecimals as formatCurrency } from "../../dependencies/custom/financeService";
import ApplyDiscountModal from "./fees/ApplyDiscountModal";
import { formatDiscountPercentage } from "./fees/discountPercentage";
import SurePayPaymentModal from "./payments/SurePayPaymentModal";

const formatAmountInput = (value) => {
  if (value === null || value === undefined || value === "") return "";

  const numericValue = Number(String(value).replace(/,/g, ""));
  if (Number.isNaN(numericValue)) return "";

  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const parseAmountInput = (value) =>
  value === null || value === undefined ? "" : value.replace(/,/g, "");

/**
 * Student Finance Drawer
 *
 * Shows complete financial breakdown for a student:
 * - School Fees (from fee structures)
 * - Custom Fees
 * - BOT Clearance Fees
 * - Transport Fees
 *
 * Displays totals for each category and grand total
 * Allows applying/editing discounts on fees
 */
const StudentFinanceDrawer = ({
  visible,
  onClose,
  studentSpc,
  studentName,
  readOnly = false,
}) => {
  const {
    studentFinances,
    loading,
    fetchStudentCompleteFinances,
    clearStudentFinances,
    applyStudentCredit,
    overrideStudentFeeAmount,
    fetchFeeAdjustmentHistory,
    applySchoolFeeDiscount,
    removeSchoolFeeDiscount,
    applyTransportFeeDiscount,
    removeTransportFeeDiscount,
    applyBotDiscount,
    removeBotDiscount,
  } = useStudentFees();

  const [discountModalVisible, setDiscountModalVisible] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [selectedFeeType, setSelectedFeeType] = useState(null);
  const [discountLoading, setDiscountLoading] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [amountOverrideModalVisible, setAmountOverrideModalVisible] =
    useState(false);
  const [historyModalVisible, setHistoryModalVisible] = useState(false);
  const [adjustmentHistory, setAdjustmentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [overrideForm] = Form.useForm();

  useEffect(() => {
    if (visible && studentSpc) {
      fetchStudentCompleteFinances(studentSpc);
    }

    // Cleanup on unmount
    return () => {
      if (!visible) {
        clearStudentFinances();
      }
    };
  }, [visible, studentSpc]);

  /**
   * Get status color
   */
  const getStatusColor = (status) => {
    const colors = {
      Pending: "orange",
      "Partially Paid": "blue",
      "Fully Paid": "green",
      Overdue: "red",
    };
    return colors[status] || "default";
  };

  /**
   * Apply the student's credit wallet to outstanding fees, then refresh.
   */
  const handleApplyCredit = async () => {
    const result = await applyStudentCredit(studentSpc);
    if (result) {
      fetchStudentCompleteFinances(studentSpc);
    }
  };

  /**
   * Open discount modal
   */
  const openDiscountModal = (fee, feeType) => {
    setSelectedFee(fee);
    setSelectedFeeType(feeType);
    setDiscountModalVisible(true);
  };

  /**
   * Close discount modal
   */
  const closeDiscountModal = () => {
    setDiscountModalVisible(false);
    setSelectedFee(null);
    setSelectedFeeType(null);
  };

  /**
   * Handle apply discount
   */
  const handleApplyDiscount = async (assignment, percentage, reason) => {
    setDiscountLoading(true);
    try {
      let result;
      // Get the correct ID field based on fee type
      let assignmentId;
      if (selectedFeeType === "bot") {
        assignmentId = assignment.id;
      } else if (selectedFeeType === "transport") {
        assignmentId = assignment.transportFeeAssignmentId;
      } else {
        assignmentId = assignment.assignmentId;
      }

      switch (selectedFeeType) {
        case "school":
          result = await applySchoolFeeDiscount(
            assignmentId,
            percentage,
            reason,
          );
          break;
        case "transport":
          result = await applyTransportFeeDiscount(
            assignmentId,
            percentage,
            reason,
          );
          break;
        case "bot":
          result = await applyBotDiscount(assignmentId, percentage, reason);
          break;
        default:
          return;
      }

      if (result) {
        closeDiscountModal();
        // Refresh finances
        fetchStudentCompleteFinances(studentSpc);
      }
    } finally {
      setDiscountLoading(false);
    }
  };

  /**
   * Handle remove discount
   */
  const handleRemoveDiscount = async (assignment, feeType) => {
    setDiscountLoading(true);
    try {
      let result;
      // Get the correct ID field based on fee type
      let assignmentId;
      if (feeType === "bot") {
        assignmentId = assignment.id;
      } else if (feeType === "transport") {
        assignmentId = assignment.transportFeeAssignmentId;
      } else {
        assignmentId = assignment.assignmentId;
      }

      switch (feeType) {
        case "school":
          result = await removeSchoolFeeDiscount(assignmentId);
          break;
        case "transport":
          result = await removeTransportFeeDiscount(assignmentId);
          break;
        case "bot":
          result = await removeBotDiscount(assignmentId);
          break;
        default:
          return;
      }

      if (result) {
        fetchStudentCompleteFinances(studentSpc);
      }
    } finally {
      setDiscountLoading(false);
    }
  };

  /**
   * Handle pay custom fee
   */
  const handlePayCustomFee = async (record) => {
    if (!record.cfpc) {
      return;
    }

    setSelectedPayment({
      paymentCode: record.cfpc,
      codeType: "CFPC",
      amount: record.balance,
      description: "Custom Fee Payment",
      feeLabel: record.feeStructureName || record.feeTypeName || "Custom Fee",
    });
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    setPaymentModalOpen(false);
    setSelectedPayment(null);
    fetchStudentCompleteFinances(studentSpc);
  };

  const openAmountOverrideModal = (record) => {
    setSelectedAssignment(record);
    overrideForm.setFieldsValue({
      amount: Number(record.amountDue || 0),
      reason: "",
    });
    setAmountOverrideModalVisible(true);
  };

  const handleAmountOverride = async () => {
    const values = await overrideForm.validateFields();
    const result = await overrideStudentFeeAmount(
      selectedAssignment.assignmentId,
      values.amount,
      values.reason,
    );
    if (result) {
      setAmountOverrideModalVisible(false);
      setSelectedAssignment(null);
      overrideForm.resetFields();
      fetchStudentCompleteFinances(studentSpc);
    }
  };

  const openAdjustmentHistory = async (record) => {
    setSelectedAssignment(record);
    setHistoryModalVisible(true);
    setHistoryLoading(true);
    setAdjustmentHistory(await fetchFeeAdjustmentHistory(record.assignmentId));
    setHistoryLoading(false);
  };

  /**
   * Render discount info
   */
  const renderDiscountInfo = (record, feeType) => {
    const hasDiscount =
      feeType === "bot"
        ? record.has_discount || record.discount_percentage > 0
        : record.hasDiscount || record.discountPercentage > 0;

    const percentage =
      feeType === "bot"
        ? record.discount_percentage
        : record.discountPercentage;

    if (!hasDiscount) {
      return <Tag>No Discount</Tag>;
    }

    return (
      <Tooltip
        title={
          feeType === "bot" ? record.discount_reason : record.discountReason
        }
      >
        <Tag color="orange">
          {formatDiscountPercentage(percentage)}% OFF
        </Tag>
      </Tooltip>
    );
  };

  /**
   * Render discount action buttons
   */
  const renderDiscountActions = (record, feeType) => {
    const hasDiscount =
      feeType === "bot"
        ? record.has_discount || record.discount_percentage > 0
        : record.hasDiscount || record.discountPercentage > 0;

    return (
      <Space size="small">
        <Tooltip title={hasDiscount ? "Edit Discount" : "Apply Discount"}>
          <Button
            type="link"
            size="small"
            icon={hasDiscount ? <EditOutlined /> : <PercentageOutlined />}
            onClick={() => openDiscountModal(record, feeType)}
          />
        </Tooltip>
        {hasDiscount && (
          <Tooltip title="Remove Discount">
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleRemoveDiscount(record, feeType)}
              loading={discountLoading}
            />
          </Tooltip>
        )}
        {feeType === "school" && record.isTuition && (
          <>
            <Tooltip title="Customize student tuition amount">
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => openAmountOverrideModal(record)}
              />
            </Tooltip>
            <Tooltip title="View amount adjustment history">
              <Button
                type="link"
                size="small"
                icon={<HistoryOutlined />}
                onClick={() => openAdjustmentHistory(record)}
              />
            </Tooltip>
          </>
        )}
      </Space>
    );
  };

  /**
   * School Fees Table Columns
   */
  const schoolFeeColumns = [
    {
      title: "Fee",
      dataIndex: "feeStructureName",
      key: "feeStructureName",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {record.feeTypeName}
          </div>
          {record.isBalanceBroughtForward && (
            <Tag color="orange" style={{ marginTop: 4 }}>
              Carried Forward
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      render: (text) => text || "Annual",
      width: 100,
    },
    {
      title: "Original",
      dataIndex: "originalAmount",
      key: "originalAmount",
      render: (amount, record) => {
        if (record.hasDiscount && amount) {
          return (
            <span style={{ textDecoration: "line-through", color: "#888" }}>
              {formatCurrency(amount)}
            </span>
          );
        }
        return "-";
      },
      align: "right",
      width: 100,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => renderDiscountInfo(record, "school"),
      width: 100,
    },
    {
      title: "Amount Due",
      dataIndex: "amountDue",
      key: "amountDue",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Amount Source",
      key: "amountSource",
      render: (_, record) =>
        record.isCustomized ? (
          <Tooltip
            title={record.customizationReason || "Student-specific amount"}
          >
            <Tag color="purple">Customized</Tag>
          </Tooltip>
        ) : (
          <Tag>Standard</Tag>
        ),
      width: 115,
    },
    {
      title: "Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 100,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <span
          style={{
            fontWeight: 500,
            color: balance > 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {formatCurrency(balance)}
        </span>
      ),
      align: "right",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: 100,
    },
    ...(!readOnly
      ? [
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => renderDiscountActions(record, "school"),
            width: 80,
          },
        ]
      : []),
  ];

  /**
   * Custom Fees Table Columns (no fee structure)
   */
  const customFeeColumns = [
    {
      title: "Fee",
      dataIndex: "feeStructureName",
      key: "feeStructureName",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || "Custom Fee"}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {record.feeTypeName}
          </div>
        </div>
      ),
    },
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      render: (text) => text || "All Terms",
      width: 100,
    },
    {
      title: "Original",
      dataIndex: "originalAmount",
      key: "originalAmount",
      render: (amount, record) => {
        if (record.hasDiscount && amount) {
          return (
            <span style={{ textDecoration: "line-through", color: "#888" }}>
              {formatCurrency(amount)}
            </span>
          );
        }
        return "-";
      },
      align: "right",
      width: 100,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => renderDiscountInfo(record, "school"),
      width: 100,
    },
    {
      title: "Amount Due",
      dataIndex: "amountDue",
      key: "amountDue",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 100,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <span
          style={{
            fontWeight: 500,
            color: balance > 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {formatCurrency(balance)}
        </span>
      ),
      align: "right",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: 100,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          {/* Pay button - only visible for parent view (readOnly=true) when status is Pending or Partially Paid */}
          {readOnly &&
            record.cfpc &&
            (record.status === "Pending" ||
              record.status === "Partially Paid") && (
              <Tooltip title="Pay">
                <Button
                  type="primary"
                  size="small"
                  icon={<CreditCardOutlined />}
                  onClick={() => handlePayCustomFee(record)}
                >
                  Pay
                </Button>
              </Tooltip>
            )}
          {/* Discount actions - only visible for admin view (!readOnly) */}
          {!readOnly && renderDiscountActions(record, "school")}
        </Space>
      ),
      width: 120,
    },
  ];

  /**
   * Transport Fees Table Columns
   */
  const transportFeeColumns = [
    {
      title: "Route",
      dataIndex: "routeName",
      key: "routeName",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {record.transportTypeName}
          </div>
        </div>
      ),
    },
    {
      title: "Term",
      dataIndex: "termName",
      key: "termName",
      width: 100,
    },
    {
      title: "Original",
      dataIndex: "originalAmount",
      key: "originalAmount",
      render: (amount, record) => {
        if (record.hasDiscount && amount) {
          return (
            <span style={{ textDecoration: "line-through", color: "#888" }}>
              {formatCurrency(amount)}
            </span>
          );
        }
        return "-";
      },
      align: "right",
      width: 100,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => renderDiscountInfo(record, "transport"),
      width: 100,
    },
    {
      title: "Amount Due",
      dataIndex: "amountDue",
      key: "amountDue",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Paid",
      dataIndex: "amountPaid",
      key: "amountPaid",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 100,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <span
          style={{
            fontWeight: 500,
            color: balance > 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {formatCurrency(balance)}
        </span>
      ),
      align: "right",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: 100,
    },
    ...(!readOnly
      ? [
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => renderDiscountActions(record, "transport"),
            width: 80,
          },
        ]
      : []),
  ];

  /**
   * BOT Clearance Table Columns
   */
  const botClearanceColumns = [
    {
      title: "Template",
      dataIndex: "template_name",
      key: "template_name",
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            BOTPC: {record.botpc}
          </div>
        </div>
      ),
    },
    {
      title: "Term",
      dataIndex: "term_name",
      key: "term_name",
      width: 100,
    },
    {
      title: "Original",
      dataIndex: "original_amount",
      key: "original_amount",
      render: (amount, record) => {
        if ((record.has_discount || record.discount_percentage > 0) && amount) {
          return (
            <span style={{ textDecoration: "line-through", color: "#888" }}>
              {formatCurrency(amount)}
            </span>
          );
        }
        return "-";
      },
      align: "right",
      width: 100,
    },
    {
      title: "Discount",
      key: "discount",
      render: (_, record) => renderDiscountInfo(record, "bot"),
      width: 100,
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 120,
    },
    {
      title: "Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 100,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (
        <span
          style={{
            fontWeight: 500,
            color: balance > 0 ? "#f5222d" : "#52c41a",
          }}
        >
          {formatCurrency(balance)}
        </span>
      ),
      align: "right",
      width: 100,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: 100,
    },
    ...(!readOnly
      ? [
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => renderDiscountActions(record, "bot"),
            width: 80,
          },
        ]
      : []),
  ];

  const creditEntryColumns = [
    {
      title: "Payment Reference",
      dataIndex: "paymentReference",
      key: "paymentReference",
      render: (reference) => reference || <Tag>Manual credit</Tag>,
      width: 170,
    },
    {
      title: "Original Credit",
      dataIndex: "originalAmount",
      key: "originalAmount",
      render: (amount) => formatCurrency(amount),
      align: "right",
      width: 130,
    },
    {
      title: "Applied",
      key: "appliedAmount",
      render: (_, entry) =>
        formatCurrency(
          Math.max(
            Number(entry.originalAmount || 0) - Number(entry.amount || 0),
            0,
          ),
        ),
      align: "right",
      width: 120,
    },
    {
      title: "Remaining",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (
        <span style={{ color: "#52c41a", fontWeight: 500 }}>
          {formatCurrency(amount)}
        </span>
      ),
      align: "right",
      width: 120,
    },
    {
      title: "Term / Year",
      key: "period",
      render: (_, entry) =>
        `${entry.term || "No term"} / ${entry.acadyr || "-"}`,
      width: 150,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (reason) => reason || "-",
      width: 220,
    },
    {
      title: "Received",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
      width: 170,
    },
  ];

  const currentTerm = studentFinances?.financialPosition?.currentTerm;
  const headlineTotal =
    studentFinances?.currentTermTotal || studentFinances?.grandTotal;
  const currentTermCredit = (
    studentFinances?.creditWallet?.entries || []
  ).reduce(
    (total, entry) =>
      currentTerm?.id && String(entry.termId) === String(currentTerm.id)
        ? total + Number(entry.amount || 0)
        : total,
    0,
  );

  return (
    <>
      <Drawer
        title={`Financial Overview - ${studentName || studentSpc}`}
        placement="right"
        onClose={onClose}
        open={visible}
        width={1100}
        styles={{ body: { paddingBottom: 80 } }}
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: "50px 0" }}>
            <Spin size="large" />
          </div>
        ) : !studentFinances ? (
          <Empty description="No financial data available" />
        ) : (
          <>
            {/* Current Term Summary */}
            <Card className="mg-b-20">
              <Row gutter={16}>
                <Col span={8}>
                  <Statistic
                    title="Current Term Due"
                    value={headlineTotal.totalDue}
                    precision={2}
                    valueStyle={{ color: "#1890ff" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Current Term Paid"
                    value={headlineTotal.totalPaid}
                    precision={2}
                    prefix={<CheckCircleOutlined />}
                    valueStyle={{ color: "#52c41a" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Current Term Balance"
                    value={headlineTotal.balance}
                    precision={2}
                    prefix={
                      headlineTotal.balance > 0 ? (
                        <WarningOutlined />
                      ) : (
                        <CheckCircleOutlined />
                      )
                    }
                    valueStyle={{
                      color: headlineTotal.balance > 0 ? "#f5222d" : "#52c41a",
                    }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
              </Row>
              <Divider style={{ margin: "16px 0" }} />
              <Row gutter={[16, 16]}>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Balance Brought Forward"
                    value={
                      studentFinances.financialPosition
                        ?.balanceBroughtForward || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#fa8c16" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Current Outstanding (Excl. B/F)"
                    value={
                      studentFinances.financialPosition?.currentOutstanding || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#1890ff" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Current Term Tuition"
                    value={
                      studentFinances.financialPosition
                        ?.currentTermOutstanding || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#1677ff" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Current Term Payable (Incl. B/F)"
                    value={
                      studentFinances.financialPosition?.currentTermPayable || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#0958d9" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Prepayment Credit"
                    value={
                      studentFinances.financialPosition?.prepaymentCredit || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#52c41a" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Gross Outstanding"
                    value={
                      studentFinances.financialPosition?.grossOutstanding || 0
                    }
                    precision={2}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
                <Col flex="1 1 180px">
                  <Statistic
                    title="Net Outstanding"
                    value={
                      studentFinances.financialPosition?.netOutstanding || 0
                    }
                    precision={2}
                    valueStyle={{ color: "#f5222d" }}
                    formatter={(value) => formatCurrency(value)}
                  />
                </Col>
              </Row>
            </Card>

            {studentFinances.financialPosition?.balanceBroughtForward > 0 && (
              <Alert
                className="mg-b-20"
                type="warning"
                showIcon
                message={`Balance Brought Forward: ${formatCurrency(studentFinances.financialPosition.balanceBroughtForward)}`}
                description="This is unpaid tuition from an earlier term and is included in the student's net outstanding amount."
              />
            )}

            {/* Credit Wallet (overpayments / prepayments) */}
            {studentFinances.creditWallet &&
              studentFinances.creditWallet.balance > 0 && (
                <Card
                  className="mg-b-20"
                  title="Credit Composition"
                  style={{ borderColor: "#52c41a" }}
                >
                  <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} md={9}>
                      <Statistic
                        title="Available Credit (All Time)"
                        value={studentFinances.creditWallet.balance}
                        precision={2}
                        valueStyle={{ color: "#52c41a" }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <span style={{ fontSize: 12, color: "#888" }}>
                        Held credit auto-applies to new terms; you can also
                        apply it now.
                      </span>
                    </Col>
                    <Col xs={24} md={9}>
                      <Statistic
                        title={`Credit Received in ${currentTerm?.name || "Current Term"}`}
                        value={currentTermCredit}
                        precision={2}
                        valueStyle={{ color: "#389e0d" }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <span style={{ fontSize: 12, color: "#888" }}>
                        Remaining credit originating in the current term.
                      </span>
                    </Col>
                    <Col xs={24} md={6} style={{ textAlign: "right" }}>
                      {!readOnly && (
                        <Button
                          type="primary"
                          loading={loading}
                          onClick={handleApplyCredit}
                        >
                          Apply Credit to Outstanding Fees
                        </Button>
                      )}
                    </Col>
                  </Row>
                  <Divider />
                  {studentFinances.creditWallet.entries.length > 0 ? (
                    <Table
                      columns={creditEntryColumns}
                      dataSource={studentFinances.creditWallet.entries}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      scroll={{ x: 1080 }}
                      summary={(entries) => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={3}>
                            <strong>Available credit from these entries</strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="right">
                            <strong style={{ color: "#52c41a" }}>
                              {formatCurrency(
                                entries.reduce(
                                  (total, entry) =>
                                    total + Number(entry.amount || 0),
                                  0,
                                ),
                              )}
                            </strong>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={4} colSpan={3} />
                        </Table.Summary.Row>
                      )}
                    />
                  ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="This wallet has a balance but no open credit entries to explain it."
                    />
                  )}
                </Card>
              )}

            {/* School Fees Section */}
            <Divider orientation="left">
              School Fees ({studentFinances.schoolFees.count})
              <span
                style={{ fontSize: "14px", color: "#888", marginLeft: "10px" }}
              >
                Balance: {formatCurrency(studentFinances.schoolFees.balance)}
              </span>
            </Divider>
            {studentFinances.schoolFees.count > 0 ? (
              <Table
                columns={schoolFeeColumns}
                dataSource={studentFinances.schoolFees.items}
                rowKey="assignmentId"
                rowClassName={(record) =>
                  record.isBalanceBroughtForward
                    ? "carried-forward-fee-row"
                    : ""
                }
                pagination={false}
                size="small"
                className="mg-b-30"
                scroll={{ x: 1000 }}
              />
            ) : (
              <Empty
                description="No school fees assigned"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="mg-b-30"
              />
            )}

            {/* Custom Fees Section */}
            <Divider orientation="left">
              Custom Fees ({studentFinances.customFees.count})
              <span
                style={{ fontSize: "14px", color: "#888", marginLeft: "10px" }}
              >
                Balance: {formatCurrency(studentFinances.customFees.balance)}
              </span>
            </Divider>
            {studentFinances.customFees.count > 0 ? (
              <Table
                columns={customFeeColumns}
                dataSource={studentFinances.customFees.items}
                rowKey="assignmentId"
                pagination={false}
                size="small"
                className="mg-b-30"
                scroll={{ x: 1000 }}
              />
            ) : (
              <Empty
                description="No custom fees assigned"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="mg-b-30"
              />
            )}

            {/* Transport Fees Section */}
            <Divider orientation="left">
              Transport Fees ({studentFinances.transportFees.count})
              <span
                style={{ fontSize: "14px", color: "#888", marginLeft: "10px" }}
              >
                Balance: {formatCurrency(studentFinances.transportFees.balance)}
              </span>
            </Divider>
            {studentFinances.transportFees.count > 0 ? (
              <Table
                columns={transportFeeColumns}
                dataSource={studentFinances.transportFees.items}
                rowKey="transportFeeAssignmentId"
                pagination={false}
                size="small"
                className="mg-b-30"
                scroll={{ x: 1000 }}
              />
            ) : (
              <Empty
                description="No transport fees assigned"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="mg-b-30"
              />
            )}

            {/* BOT Clearance Fees Section */}
            <Divider orientation="left">
              BOT Clearance Fees ({studentFinances.botClearances.count})
              <span
                style={{ fontSize: "14px", color: "#888", marginLeft: "10px" }}
              >
                Balance: {formatCurrency(studentFinances.botClearances.balance)}
              </span>
            </Divider>
            {studentFinances.botClearances.count > 0 ? (
              <Table
                columns={botClearanceColumns}
                dataSource={studentFinances.botClearances.items}
                rowKey="id"
                pagination={false}
                size="small"
                className="mg-b-30"
                scroll={{ x: 1000 }}
              />
            ) : (
              <Empty
                description="No BOT clearance fees assigned"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                className="mg-b-30"
              />
            )}
          </>
        )}
      </Drawer>

      {/* Apply Discount Modal - only shown in non-readonly mode */}
      {!readOnly && (
        <ApplyDiscountModal
          visible={discountModalVisible}
          onClose={closeDiscountModal}
          onApply={handleApplyDiscount}
          feeType={selectedFeeType}
          assignment={selectedFee}
          loading={discountLoading}
        />
      )}

      {!readOnly && (
        <Modal
          title="Customize Student Tuition Amount"
          open={amountOverrideModalVisible}
          onCancel={() => setAmountOverrideModalVisible(false)}
          onOk={handleAmountOverride}
          confirmLoading={loading}
          okText="Save Amount"
        >
          <Form form={overrideForm} layout="vertical">
            <Form.Item
              label="Student"
              help={selectedAssignment?.feeStructureName}
            >
              <Input value={studentName || studentSpc} disabled />
            </Form.Item>
            <Form.Item
              name="amount"
              label="Revised tuition amount"
              rules={[
                { required: true, message: "Enter the revised amount" },
                { type: "number", min: 0 },
              ]}
            >
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                precision={2}
                formatter={formatAmountInput}
                parser={parseAmountInput}
                inputMode="decimal"
              />
            </Form.Item>
            <Form.Item
              name="reason"
              label="Reason"
              rules={[
                { required: true, message: "Enter the reason for this change" },
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder="Explain why this student has a different amount"
              />
            </Form.Item>
          </Form>
        </Modal>
      )}

      <Modal
        title={`Tuition Amount History${selectedAssignment ? ` - ${selectedAssignment.feeStructureName || ""}` : ""}`}
        open={historyModalVisible}
        onCancel={() => setHistoryModalVisible(false)}
        footer={null}
      >
        <Table
          loading={historyLoading}
          rowKey="adjustmentId"
          size="small"
          pagination={false}
          dataSource={adjustmentHistory}
          columns={[
            {
              title: "Original",
              dataIndex: "originalAmount",
              render: (value) => formatCurrency(value),
            },
            {
              title: "Revised",
              dataIndex: "revisedAmount",
              render: (value) => formatCurrency(value),
            },
            { title: "Reason", dataIndex: "reason" },
            { title: "User", dataIndex: "adjustedByName" },
            {
              title: "Date",
              dataIndex: "adjustedAt",
              render: (value) =>
                value ? new Date(value).toLocaleString() : "-",
            },
          ]}
        />
      </Modal>

      <SurePayPaymentModal
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        onSuccess={handlePaymentSuccess}
        paymentCode={selectedPayment?.paymentCode}
        codeType={selectedPayment?.codeType}
        amount={selectedPayment?.amount}
        description={selectedPayment?.description}
        studentName={studentName}
        feeLabel={selectedPayment?.feeLabel}
        title="Pay Custom Fee"
      />
    </>
  );
};

export default StudentFinanceDrawer;
